<template>
  <div class="content-video">
    <div class="content-video__frame">
      <SbIcon
        name="scribble"
        color="info-bold"
        size="large"
        class="content-video__icon-top"
      />

      <SbIcon
        name="highlight"
        color="info-bold"
        size="large"
        class="content-video__icon-bottom"
      />

      <SbIllustration
        class="content-video__bg"
        src="https://a.storyblok.com/f/136045/400x398/3517d9b879/video-labs-bg.svg"
        width="40%"
      />

      <div class="content-video__media">
        <img
          v-if="!isPlaying && blok.cover"
          :alt="blok.cover.alt"
          class="content-video__cover"
          :src="`${blok.cover.filename}/m/731x411/`"
          :srcset="`${blok.cover.filename}/m/1462x822/ 2x`"
        />
        <div v-if="isPlaying && videoUrl" class="content-video__player">
          <iframe
            :src="videoUrl"
            :title="$t('YouTube video player')"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            width="640"
            height="390"
          />
        </div>
      </div>
      <SbIconButton
        v-if="!isPlaying"
        icon="play-button"
        :is-clear="false"
        is-rounded
        :label="$t('Play the video')"
        size="normal"
        variant="tertiary"
        class="content-video__icon-play"
        @click="playVideo"
      />
    </div>

    <BaseStoryblokRichText
      v-if="hasCaption"
      class="content-video__caption"
      :doc="blok.caption"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { type ISbRichtext } from 'storyblok-js-client'
import { useTracking } from '@/store/modules/App/tracking'
import { isValidUrl } from '@/utils/checkers'
import { SbIcon, SbIllustration, SbIconButton } from '@storyblok/design-system'
import BaseStoryblokRichText from '@/components/Base/BaseStoryblokRichText/BaseStoryblokRichText.vue'

const { analyticsTrack } = useTracking()

const isPlaying = ref(false)

const props = defineProps<{
  blok: {
    url: string
    cover: {
      filename: string
      alt: string
    }
    caption: ISbRichtext
  }
  title: string
  id: string
}>()

const hasCaption = computed(
  () =>
    props.blok.caption?.content?.[0]?.content &&
    props.blok.caption.content[0].content.length > 0,
)

const videoUrl = computed(() => {
  return props.blok.url && isValidUrl(props.blok.url)
    ? createUrlWithQuery(props.blok.url, { autoplay: 1, start: 0, rel: 0 })
    : ''
})

function createUrlWithQuery(
  baseUrl: string,
  params: Record<string, number | string>,
) {
  const url = new URL(baseUrl)
  Object.keys(params).forEach((key) => {
    url.searchParams.append(key, String(params[key]))
  })
  return url.toString()
}

const playVideo = () => {
  isPlaying.value = true
  if (props.id) {
    analyticsTrack({
      event: 'click_labs_detail_page_video',
      payload: {
        feature_name: props.title,
        context: 'labs',
      },
    })
  } else {
    console.warn('No tracking id provided for video')
  }
}
</script>

<style lang="scss" src="./content-video.scss"></style>
